import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Divider } from '@mui/material';
import { numberToWords } from './utils';
import ThermalContentHeader from './ThermalContentHeader';

export default function LabInvoiceThermalContent({ customerInfo, invoiceData }) {
  if (!invoiceData) {
    return null;
  }

  const {
    items: { lab_tests = [], platform_fee: platformFeeItems = [] } = {}
  } = invoiceData;

  const processedItems = () => {
    if (!lab_tests.length) return { activeItems: [], cancelledItems: [] };

    const hasCancelledItems = lab_tests.some(item => item.status === 'cancelled');
    const hasStatusField = lab_tests.some(item => item.status);

    if (!hasStatusField) {
      return { activeItems: lab_tests, cancelledItems: [] };
    }

    if (hasCancelledItems) {
      const itemsMap = {};
      lab_tests.forEach(item => {
        const itemId = item.id;
        if (!itemsMap[itemId]) {
          itemsMap[itemId] = {
            id: itemId,
            name: item.name,
            selling_price: parseFloat(item.selling_price || 0),
            activeQuantity: 0,
            cancelledQuantity: 0,
            discount_in_perc: item.discount_in_perc || 0,
            created_at: item.created_at,
            batch_number: item.batch_number,
            expiry_date: item.expiry_date,
            manufacturing_date: item.manufacturing_date
          };
        }

        if (item.status === 'active') {
          itemsMap[itemId].activeQuantity = Number(item.quantity);
        } else if (item.status === 'cancelled') {
          itemsMap[itemId].cancelledQuantity += Number(item.quantity);
        }
      });

      const activeItems = Object.values(itemsMap)
        .map(item => {
          const netQuantity = item.activeQuantity - item.cancelledQuantity;
          return {
            ...item,
            quantity: netQuantity
          };
        })
        .filter(item => item.quantity > 0);

      const cancelledItems = lab_tests.filter(item => item.status === 'cancelled');
      return { activeItems, cancelledItems };
    } else {
      const activeItems = lab_tests.filter(item => item.status === 'active');
      const cancelledItems = [];
      return { activeItems, cancelledItems };
    }
  };

  const { activeItems, cancelledItems } = processedItems();

  const calcFinal = (price, discount) => price * (1 - (discount || 0) / 100);

  // calculate subtotal including ALL items (active + cancelled)
  const allItems = [...activeItems, ...cancelledItems];
  const subtotalBeforeDiscount = allItems.reduce((sum, item) => sum + (Number(item.selling_price || 0) * Number(item.quantity || 1)), 0);
  
  const totalDiscountAmount = allItems.reduce((sum, item) => 
    sum + ((Number(item.selling_price || 0) * Number(item.quantity || 1)) * Number(item.discount_in_perc || 0)) / 100, 0);
  const actualDiscountPercentage = subtotalBeforeDiscount > 0 
    ? (totalDiscountAmount / subtotalBeforeDiscount) * 100 
    : 0;
  const subtotalAfterDiscount = subtotalBeforeDiscount - totalDiscountAmount;

  const cancelledTotal = cancelledItems.reduce((sum, item) => 
    sum + calcFinal(Number(item.selling_price || 0) * Number(item.quantity || 1), Number(item.discount_in_perc || 0)), 0);

  // Online bookings bill the platform fee as its own item; it is already in the grand total.
  const platformFee = platformFeeItems.reduce((sum, item) => sum + Number(item.net_price ?? item.selling_price ?? 0), 0);
  const roundOff = invoiceData.roundOff !== undefined 
    ? invoiceData.roundOff 
    : Math.abs(Number(invoiceData.round_off || 0));
  const isPositiveRoundOff = invoiceData.isPositiveRoundOff !== undefined 
    ? invoiceData.isPositiveRoundOff 
    : (Number(invoiceData.original_amount || 0) > Number(invoiceData.total_amount || 0));
  
  const finalAmountToPay = invoiceData.finalTotal !== undefined 
    ? invoiceData.finalTotal 
    : Number(invoiceData.total_amount || 0);
  
  // Intentional: Numeric grand total is stored/displayed as float, but textual representation is rounded to nearest integer for readability.
  const totalInWords = `Rs. ${numberToWords(Math.round(finalAmountToPay))} Only`;
  const anyDiscount = activeItems.some(item => item.discount_in_perc > 0);

  // Group cancelled items by return event (cancellation_ledger_id)
  const groupedCancelledEvents = (() => {
    if (!cancelledItems || cancelledItems.length === 0) return [];

    const eventsMap = {};
    cancelledItems.forEach(item => {
      const ledgerId = item.cancellation_ledger_id || 'unknown';
      const timeStr = item.created_at ? format(new Date(item.created_at), 'dd-MM-yyyy HH:mm') : 'N/A';
      const staffName = item.cancelled_by_name || 'N/A';

      if (!eventsMap[ledgerId]) {
        eventsMap[ledgerId] = {
          time: timeStr,
          staffName: staffName,
          items: []
        };
      }
      eventsMap[ledgerId].items.push(item);
    });
    return Object.values(eventsMap);
  })();

  return (
    <Box
      sx={{
        maxWidth: "72mm",
        width: "72mm",
        fontSize: "9px",
        "& .MuiTableCell-root": { p: "2px", fontSize: "13px" }, // Table cells padding
        "@media print": { fontSize: "13px" },
      }}
    >
      <ThermalContentHeader invoiceData={invoiceData} customerInfo={customerInfo} />

      {/* Table of Tests */}
      <TableContainer>
        <Table size="small">
          <TableHead style={{ borderTopColor: 'black', borderBottomColor: 'black' }}>
            <TableRow>
              <TableCell style={{ fontSize: "10px", paddingLeft: '0' }}>#</TableCell>
              <TableCell style={{ fontSize: "10px" }}>Description</TableCell>
              <TableCell style={{ fontSize: "10px" }} align="center">Qty</TableCell>
              <TableCell style={{ fontSize: "10px" }} align="center">MRP</TableCell>
              {anyDiscount && (
                <TableCell style={{ fontSize: "10px" }} align="center">Disc%</TableCell>
              )}
              <TableCell style={{ fontSize: "10px", paddingRight: '0' }} align="center">Amt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Active Items */}
            {activeItems.map((item, i) => (
              <TableRow key={i}>
                <TableCell style={{ fontSize: "10px", paddingLeft: '0' }}>{i + 1}</TableCell>
                <TableCell style={{ 
                  fontSize: "10px", 
                  overflowWrap: 'break-word',
                  wordBreak: 'break-word',
                  whiteSpace: 'normal',
                  maxWidth: '25ch'
                }}>{item.name}</TableCell>
                <TableCell align="center" style={{ fontSize: "10px" }}>
                  {item.quantity || 1}
                </TableCell>
                <TableCell align="center" style={{ fontSize: "10px" }}>
                  {parseFloat(item.selling_price).toFixed(2)}
                </TableCell>
                {anyDiscount && (
                  <TableCell align="center" style={{ fontSize: "10px" }}>
                    {parseFloat(item.discount_in_perc || 0).toFixed(2)}%
                  </TableCell>
                )}
                <TableCell align="center" style={{ fontSize: "10px", paddingRight: '0' }}>
                  {parseFloat(calcFinal(item.selling_price * (item.quantity || 1), item.discount_in_perc)).toFixed(2)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Cancelled Items (if any) */}
      {groupedCancelledEvents.length > 0 && (() => {
        const grandCancelTotal = groupedCancelledEvents.reduce((gsum, evt) =>
          gsum + evt.items.reduce((sum, item) =>
            sum + calcFinal(Number(item.selling_price || 0) * Number(item.quantity || 1), Number(item.discount_in_perc || 0)), 0), 0);

        return (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1, mb: 0.3 }}>
              <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>
                SALES RETURNS ({groupedCancelledEvents.length})
              </Typography>
              <Typography sx={{ fontWeight: 'bold', fontSize: '10px' }}>
                Total: ₹{grandCancelTotal.toFixed(2)}
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '9px', p: '1px 2px' } }}>
                <TableHead>
                  <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 'bold', borderBottom: '1px solid black' } }}>
                    <TableCell style={{ paddingLeft: '0', width: '6%' }}>#</TableCell>
                    <TableCell style={{ width: '42%' }}>Description</TableCell>
                    <TableCell align="center" style={{ width: '12%' }}>Qty</TableCell>
                    <TableCell align="center" style={{ width: '18%' }}>MRP</TableCell>
                    {anyDiscount && <TableCell align="center" style={{ width: '10%' }}>Disc%</TableCell>}
                    <TableCell align="right" style={{ paddingRight: '0', width: '22%' }}>Amt</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {groupedCancelledEvents.map((evt, evtIdx) => {
                    const eventTotal = evt.items.reduce((sum, item) =>
                      sum + calcFinal(Number(item.selling_price || 0) * Number(item.quantity || 1), Number(item.discount_in_perc || 0)), 0);
                    return (
                      <>
                        <TableRow key={`evt-hdr-${evtIdx}`} sx={{ backgroundColor: '#f0f0f0' }}>
                          <TableCell colSpan={anyDiscount ? 4 : 3} style={{ paddingLeft: '0', fontStyle: 'italic' }}>
                            #{evtIdx + 1} {evt.time}
                          </TableCell>
                          <TableCell colSpan={2} align="right" style={{ paddingRight: '0', fontStyle: 'italic', fontSize: '8px' }}>
                            By: {evt.staffName || 'N/A'}
                          </TableCell>
                        </TableRow>
                        {evt.items.map((item, i) => (
                          <TableRow key={`cancelled-${evtIdx}-${i}`}>
                            <TableCell style={{ paddingLeft: '0' }}>{i + 1}</TableCell>
                            <TableCell style={{ overflowWrap: 'break-word', wordBreak: 'break-word', whiteSpace: 'normal', maxWidth: '20ch' }}>{item.name}</TableCell>
                            <TableCell align="center">{item.quantity || 1}</TableCell>
                            <TableCell align="center">{parseFloat(item.selling_price).toFixed(2)}</TableCell>
                            {anyDiscount && <TableCell align="center">{parseFloat(item.discount_in_perc || 0).toFixed(2)}%</TableCell>}
                            <TableCell align="right" style={{ paddingRight: '0', fontWeight: 600 }}>
                              {parseFloat(calcFinal(item.selling_price * (item.quantity || 1), item.discount_in_perc)).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))}
                        {groupedCancelledEvents.length > 1 && (
                          <TableRow key={`evt-total-${evtIdx}`}>
                            <TableCell colSpan={anyDiscount ? 5 : 4} align="right" style={{ fontStyle: 'italic' }}>sub:</TableCell>
                            <TableCell align="right" style={{ paddingRight: '0', fontWeight: 'bold' }}>₹{eventTotal.toFixed(2)}</TableCell>
                          </TableRow>
                        )}
                      </>
                    );
                  })}
                  <TableRow sx={{ borderTop: '1px solid black' }}>
                    <TableCell colSpan={anyDiscount ? 5 : 4} align="right" style={{ fontWeight: 'bold', paddingTop: '2px' }}>Total Returned:</TableCell>
                    <TableCell align="right" style={{ paddingRight: '0', fontWeight: 'bold', paddingTop: '2px' }}>₹{grandCancelTotal.toFixed(2)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </>
        );
      })()}

      <Divider sx={{ my: 0.5, borderColor: 'black' }} />

      {/* Billing Summary */}
      <Box sx={{ textAlign: 'right', fontSize: '11px' }}>
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mb: 0.5,
          mt: 0.5,
          py: 0.5,
          px: 0.5,
          backgroundColor: '#f5f5f5',
          borderTop: '1px solid black',
          borderBottom: '1px solid black'
        }}>
          <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>Subtotal (Before Discount)</Typography>
          <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>₹{subtotalBeforeDiscount.toFixed(2)}</Typography>
        </Box>
        
        {anyDiscount && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
            <Typography sx={{ fontSize: '11px' }}>(-) Discount ({actualDiscountPercentage.toFixed(2)}%)</Typography>
            <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>₹{totalDiscountAmount.toFixed(2)}</Typography>
          </Box>
        )}
        
        <Divider sx={{ my: 0.3, borderStyle: 'dashed' }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
          <Typography sx={{ fontSize: '11px' }}>Subtotal (After Discount)</Typography>
          <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>₹{subtotalAfterDiscount.toFixed(2)}</Typography>
        </Box>
        
        {cancelledItems.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
            <Typography sx={{ fontSize: '11px' }}>(-) Cancelled Items (+ Discounted)</Typography>
            <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>₹{cancelledTotal.toFixed(2)}</Typography>
          </Box>
        )}
        {platformFee > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
            <Typography sx={{ fontSize: '11px' }}>(+) Online Platform Fee</Typography>
            <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>₹{platformFee.toFixed(2)}</Typography>
          </Box>
        )}
        
        {roundOff > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
            <Typography sx={{ fontSize: '11px' }}>Round Off</Typography>
            <Typography sx={{ fontSize: '11px', fontWeight: 'bold' }}>{isPositiveRoundOff ? '+' : '-'}₹{roundOff.toFixed(2)}</Typography>
          </Box>
        )}
        
        <Divider sx={{ my: 0.3, borderStyle: 'dashed' }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>Final Amount to Pay (Grand Total)</Typography>
          <Typography sx={{ fontSize: '12px', fontWeight: 'bold' }}>₹{finalAmountToPay.toFixed(2)}</Typography>
        </Box>
      </Box>

      <Typography sx={{ textAlign: "center", fontSize: "13px", fontStyle: 'italic' }}>
        {totalInWords}
      </Typography>

      <Divider sx={{ my: 0.5, borderColor: 'black' }} />

      {/* Footer */}
      <Typography sx={{ mt: 1, fontWeight: 'bold', textAlign: "center", fontSize: "13px" }}>
        नर सेवा नारायण सेवा
      </Typography>
    </Box>
  );
}

LabInvoiceThermalContent.propTypes = {
  customerInfo: PropTypes.object.isRequired,
  invoiceData: PropTypes.object.isRequired
};
